import { CategoryInput, HotkeysRegistry } from './hotkeys.types';

/**
 * Generic helper: returns the raw key string for a hotkey by category and id from any hotkeys config,
 * or undefined if not found. createHotkeys() uses this to implement .get(category, id) on the config.
 */
export function getHotkey<C extends Record<string, { hotkeys: Record<string, { key: string }> }>>(
  config: C,
  category: keyof C,
  id: string,
): string | undefined {
  const categoryDef = config[category];
  const hotkeys = categoryDef?.hotkeys as Record<string, { key: string }>;

  return hotkeys?.[id]?.key ?? '';
}

/**
 * Creates a hotkeys config and attaches a .get(category, id) method that returns the key string.
 * The returned object is a Proxy so Object.entries() only sees category keys (categorizeHotkeys still works).
 */
export function createHotkeys<V extends Record<string, CategoryInput>>(v: V): HotkeysRegistry<V> {
  const get = (category: keyof V, id: string) => getHotkey(v, category, id);

  return new Proxy(v, {
    get(target, prop) {
      if (prop === 'get') return get;
      return Reflect.get(target, prop);
    },
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
  }) as HotkeysRegistry<V>;
}
